import { Component, Inject, OnInit } from '@angular/core';
import type { Logger } from '@core/model/domain/logger';
import { Option } from '@core/model/framework/Result/Option';
import { GetBankById } from '@core/usecase/getBankById';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { TranslateModule } from '@ngx-translate/core';
import { TrackerService } from '@shared/services/tracker.service';
import { loggerToken } from 'src/conf/di/angularProviderMapper';
import { RelayBank, WebauthRedirectVM } from './webauth-redirect.viewmodel';

/*
  Loading page of the bank tab (or in-place navigation) opened on bank choice.
  Autonomous: reads its journey data from the URL fragment, shows the bank,
  chooses the completion mode, and redirects to the bank — see WebauthRedirectVM.
*/
@Component({
  selector: 'app-webauth-redirect',
  templateUrl: './webauth-redirect.component.html',
  standalone: true,
  imports: [TranslateModule],
})
export class WebauthRedirectComponent implements OnInit {
  private readonly webauthRedirectVM: WebauthRedirectVM;

  constructor(
    private readonly getWebauthUrl: GetWebauthUrl,
    private readonly getBankById: GetBankById,
    private readonly tracker: TrackerService,
    @Inject(loggerToken) private readonly loggerService: Logger
  ) {
    this.webauthRedirectVM = new WebauthRedirectVM(this.getWebauthUrl, this.getBankById, this.loggerService);
  }

  async ngOnInit(): Promise<void> {
    // Local runs only: nothing happens for a while, the page is inspectable as opened.
    await this.webauthRedirectVM.holdForLocalInspection();

    // Strip the token-bearing fragment from the url BEFORE trackPage, which
    // beacons window.location.href to the telemetry backend.
    const fragment = this.webauthRedirectVM.takeFragment();
    this.tracker.trackPage({ taggingPage: 'WEBAUTH_REDIRECT' });
    // Displayed while the bank url is fetched; the journey never waits for it.
    void this.webauthRedirectVM.showBank(fragment);
    await this.webauthRedirectVM.drive(fragment);
  }

  get bank(): Option<RelayBank> {
    return this.webauthRedirectVM.bank;
  }
}
